import { PasswordInput } from '@rentnerkev/inputs/password-input'
import { TextInput } from '@rentnerkev/inputs/text-input'
import { Link } from '@tanstack/react-router'
import { Lock, User } from 'lucide-react'

import useTranslationStore from '@/language/useTranslationStore'

import { LoginHeader } from './Components/LoginHeader'
import TwoFactorForm from './Components/TwoFactorForm'
import useLoginLogic from './Hooks/useLoginLogic'
import type { LoginPageProps } from './Types/types.ts'

export default function LoginPage({ redirectReason }: LoginPageProps) {
    const { t } = useTranslationStore()
    const { form, state, handler, setter, refs } = useLoginLogic(redirectReason)

    return (
        <div className="flex w-full items-center justify-center">
            <div className="border-border-dark bg-surface-dark/95 w-full max-w-md rounded-xl border p-8 backdrop-blur">
                {state.requiresTwoFactor ? (
                    <TwoFactorForm
                        code={state.twoFactorCode}
                        isLoading={state.isLoading}
                        isError={state.isTwoFactorError}
                        trustThisDevice={state.trustThisDevice}
                        onSubmit={handler.handleTwoFactorSubmit}
                        onChange={handler.handleTwoFactorChange}
                        inputRef={refs.twoFactorInput}
                        onTrustThisDeviceChange={setter.setTrustThisDevice}
                        onBackToLogin={handler.handleBackToLogin}
                    />
                ) : (
                    <>
                        <LoginHeader />

                        <form
                            onSubmit={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                form.handleSubmit()
                            }}
                            className="space-y-5"
                        >
                            <form.Field
                                name="username"
                                children={(field) => (
                                    <TextInput
                                        name={field.name}
                                        onBlur={field.handleBlur}
                                        aria-label={t(
                                            'auth.login.usernamePlaceholder',
                                        )}
                                        id={field.name}
                                        value={field.state.value}
                                        required
                                        onValueChange={field.handleChange}
                                        placeholder={t(
                                            'auth.login.usernamePlaceholder',
                                        )}
                                        icon={<User className="h-5 w-5" />}
                                        maxLength={50}
                                        showLength={true}
                                    />
                                )}
                            />
                            <form.Field
                                name="password"
                                children={(field) => (
                                    <PasswordInput
                                        name={field.name}
                                        onBlur={field.handleBlur}
                                        aria-label={t(
                                            'auth.login.passwordPlaceholder',
                                        )}
                                        value={field.state.value}
                                        onValueChange={field.handleChange}
                                        placeholder={t(
                                            'auth.login.passwordPlaceholder',
                                        )}
                                        required
                                        maxLength={132}
                                        id={field.name}
                                        icon={<Lock className="h-5 w-5" />}
                                    />
                                )}
                            />
                            <div className="mt-2 flex items-center justify-end">
                                <Link
                                    to="/forgot-password"
                                    className="text-primary hover:text-primary-hover text-sm font-medium hover:underline"
                                >
                                    {t('auth.login.forgotPassword')}
                                </Link>
                            </div>

                            <button
                                type="submit"
                                disabled={state.isLoading}
                                className="bg-primary hover:bg-primary-hover focus-visible:outline-primary mt-6 flex w-full cursor-pointer justify-center rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {state.isLoading
                                    ? t('auth.login.loggingIn')
                                    : t('auth.login.login')}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    )
}
